<template>
  <div>
    <!-- Resumo -->
    <div class="row mt-4">
      <div class="col-md-3" v-for="card in summaryCards" :key="card.label">
        <div class="ibox summary-card">
          <div class="ibox-content text-center">
            <h2 :class="card.class">{{ card.value }}</h2>
            <small>{{ card.label }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Motivos: cada um vira um atalho de seleção -->
    <div class="row" v-if="reasons.length">
      <div class="col-md-12">
        <span class="mr-2"><strong>Desativados por motivo:</strong></span>
        <button
          v-for="reason in reasons"
          :key="reason.name"
          class="btn btn-sm btn-outline mr-2 mb-2"
          @click="selectByReason(reason.name)"
        >
          {{ reason.name }} ({{ reason.count }})
        </button>
      </div>
    </div>
    <hr>

    <!-- Busca -->
    <div class="row mt-4">
      <div class="col-md-8">
        <input
          class="input form-control"
          @keypress.enter="find()"
          v-model="query.generic"
          placeholder="Pesquisar por login"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <!-- col-md-4 fecha as 12 colunas: com col-md-3 sobrava uma coluna morta e o select não
           alinhava com a borda direita dos cards de resumo. -->
      <div class="col-md-4">
        <select v-model="query.active" class="form-control" @change="find()">
          <option v-for="opt in filterActive.table" :value="opt.value" :key="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>
    </div>
    <hr>

    <!-- Ações em lote -->
    <div class="row mt-3" v-if="checkedLogins.length">
      <div class="col-md-12">
        <strong class="mr-3">{{ checkedLogins.length }} selecionado(s)</strong>

        <button class="btn btn-primary mr-2" @click="confirmReactivate">
          Reativar selecionados
        </button>

        <span v-if="needCredentialCount" class="text-warning">
          <alert-circle-icon class="icon-inline"/>
          {{ needCredentialCount }} com credencial recusada — vão cair de novo sem o cliente
          corrigir a senha.
        </span>
      </div>
    </div>

    <!-- Tabela -->
    <div class="row">
      <div class="col-sm-12">
        <div class="row mt-4">
          <div class="col-md-12">
            <strong class="mr-3">{{ infoTotalModels }}</strong>
          </div>
        </div>

        <table class="table table-borderless table-align table-h51">
          <thead>
            <tr>
              <th>
                <BkpsCheckBox
                  :value="checkAll"
                  @input="changeCheck({ sources: logins, checkAll: !checkAll })"
                />
              </th>
              <th>Login</th>
              <th>Conta</th>
              <th>Status</th>
              <th>Motivo</th>
              <th v-tooltip.down="'Tentativas do ciclo em andamento: Platform / Único. Zera quando o ciclo termina.'">
                Tentativas
              </th>
              <th>Atualizado em</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="login in logins" :key="login.id">
              <td :class="buildClass(login)">
                <BkpsCheckBox v-model="login.checked" @input="changeCheck({ sources: logins })"/>
              </td>
              <td>{{ login.login }}</td>
              <td>{{ getAccountName(login) }}</td>
              <!-- "executando" só faz sentido para login ativo: em desativado seria a flag presa
                   de um ciclo que morreu no meio. -->
              <td>
                <span :class="login.active ? 'text-success' : 'text-danger'">
                  {{ login.active ? 'Ativo' : 'Desativado' }}
                </span>
                <small v-if="login.active && login.running" class="d-block text-muted">
                  buscando notas
                </small>
              </td>
              <td>
                <span v-if="login.deactivationReason">
                  {{ login.deactivationReason }}
                  <alert-circle-icon
                    v-if="login.requiresNewCredential"
                    class="icon-inline text-warning"
                    v-tooltip.down="'Reativar não resolve: o cliente precisa corrigir a senha.'"
                  />
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ attemptsOf(login) }}</td>
              <td>{{ login.updatedAt | datetime }}</td>
              <td>
                <button class="btn btn-sm btn-outline mr-1" @click="showDetail(login)">
                  Erros
                </button>
                <button class="btn btn-sm btn-outline mr-1" @click="reprocess(login)">
                  Reprocessar
                </button>
                <button class="btn btn-sm btn-outline" @click="goToEvents(login)">
                  Eventos
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de erros -->
    <Modal v-if="currentLogin" v-model="showModal">
      <div slot="header">
        <p><strong>{{ currentLogin.login }}</strong></p>
      </div>

      <p><strong>Motivo: </strong>{{ currentLogin.deactivationReason || '—' }}</p>
      <p><strong>Última busca: </strong>{{ currentLogin.setting.lastDate }}</p>
      <p><strong>Próxima iteração: </strong>{{ currentLogin.setting.nextSearch }}</p>

      <hr>
      <h3>Erros</h3>
      <textarea
        :value="lastErrorsOf(currentLogin)"
        class="form-control"
        disabled="true"
        rows="6"
      />
    </Modal>
  </div>
</template>

<script>
import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
import CheckedModels from '@/mixins/CheckedModels'
import Modal from '@/components/shares/specialTag/Modal'
import ModelMapper from '@/mixins/ModelMapper'
import Pagination from '@/components/shares/specialTag/Pagination'
import { AlertCircleIcon, SearchIcon } from 'vue-feather-icons'
import { Filter } from '@/models/filter'

export default {
  components: { AlertCircleIcon, BkpsCheckBox, Modal, Pagination, SearchIcon },
  computed: {
    checkedLogins() {
      return this.logins.filter(login => login.checked)
    },
    logins() {
      return this.getModels || []
    },
    // `requiresNewCredential` vem do backend: o frontend não decide quais motivos justificam
    // desativação, só conta o que já veio classificado.
    needCredentialCount() {
      return this.checkedLogins.filter(login => login.requiresNewCredential).length
    },
    reasons() {
      const byReason = this.summary.byReason || {}
      return Object.keys(byReason).map(name => ({ name: name, count: byReason[name] }))
    },
    summaryCards() {
      return [
        { label: 'Total', value: this.summary.total || 0, class: '' },
        { label: 'Ativos', value: this.summary.active || 0, class: 'text-success' },
        { label: 'Desativados', value: this.summary.inactive || 0, class: 'text-danger' },
        {
          label: 'Desativados nas últimas 24h',
          value: this.summary.inactiveLast24Hours || 0,
          class: 'text-warning'
        }
      ]
    }
  },
  created() {
    this.addOrderBy('updatedAt')
    this.$set(this.query, 'active', 'false')

    this.defaultModelSetting.updateQueryURL = true
    this.asyncTemplateSelect({ byURL: true, pagination: true })
    this.loadSummary()
  },
  data: () => ({
    currentLogin: null,
    filterActive: new Filter({
      key: 'active',
      label: 'Status',
      table: [['Desativados', 'false'], ['Ativos', 'true'], ['Todos', '']]
    }),
    modelPathPrefix: 'admin/unico_integration',
    nameModel: 'Login',
    showModal: false,
    summary: {}
  }),
  inject: ['getAccountName'],
  methods: {
    // Os contadores são zerados quando o ciclo se encerra, então em login desativado são sempre
    // 0/0. Mostrar "—" nesse caso evita parecer que o número está quebrado; quando aparece
    // preenchido, é um login retentando agora.
    attemptsOf(login) {
      const setting = login.setting || {}
      const platform = setting.attempts || 0
      const token = setting.tokenAttempts || 0

      return platform || token ? `${platform} / ${token}` : '—'
    },
    buildClass(login) {
      if (!login.active) return 'marker-red'
      if (login.deactivationReason) return 'marker-yellow'
    },
    confirmReactivate() {
      const total = this.checkedLogins.length
      let message = `Reativar ${total} login(s)?`

      if (this.needCredentialCount)
        message += ` ${this.needCredentialCount} têm credencial recusada e devem cair novamente.`

      if (window.confirm(message)) this.reactivate()
    },
    find() {
      this.asyncTemplateSelect()
    },
    // `start_date` vazio de propósito: a tela de Eventos filtra o dia atual por padrão, e o que
    // interessa aqui é o histórico do login.
    goToEvents(login) {
      this.$router.push({
        path: '/admin/unico_integration/events',
        query: { 'q[login_id]': login.id, 'q[start_date]': '' }
      })
    },
    lastErrorsOf(login) {
      const errors = (login.setting || {}).lastErrors
      return Array.isArray(errors) ? errors.join('\n') : ''
    },
    // O interceptor do axios já devolve o corpo da resposta, então `response` é o
    // `{ status, data, errors, meta }` — o resumo está em `response.data`, sem um nível extra.
    loadSummary() {
      this.axios.get('/admin/unico_integration/logins/summary')
        .then(response => { this.summary = this.$parse.toCamelcase(response.data || {}) })
    },
    reactivate() {
      const ids = this.checkedLogins.map(login => login.id)

      this.openLoad()
      this.axios.put('/admin/unico_integration/logins/reactivate', { ids: ids })
        .then(() => {
          this.$notify.add(`${ids.length} login(s) reativado(s)!`)
          this.changeCheck({ sources: this.logins, checkAll: false })
          this.refresh()
        })
        .finally(() => this.closeLoad())
    },
    refresh() {
      this.asyncTemplateSelect()
      this.loadSummary()
    },
    reprocess(login) {
      this.openLoad()
      this.axios.put(`/admin/unico_integration/logins/${login.id}/reprocess`)
        .then(() => this.$notify.add(`${login.login} enfileirado para reprocessamento!`))
        .finally(() => this.closeLoad())
    },
    // Seleciona todos os desativados por um motivo: é o que torna a reativação em lote segura,
    // em vez de marcar tudo sem olhar a causa.
    selectByReason(reason) {
      this.logins.forEach(login => {
        this.$set(login, 'checked', login.deactivationReason === reason)
      })
    },
    showDetail(login) {
      this.currentLogin = login
      this.showModal = true
    }
  },
  mixins: [CheckedModels, ModelMapper],
  name: 'Login'
}
</script>

<style scoped>
  .summary-card h2 {
    margin-bottom: 0;
  }

  .icon-inline {
    height: 16px;
    width: 16px;
  }
</style>
